import { Badge, Box, Button, Card, DataList, Flex, Grid, Inset } from '@radix-ui/themes'
import { Link, useOutletContext } from '@remix-run/react'
import cn from 'clsx'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { ProductName } from '~/components/common/product-name'
import { Image, IMAGE_FILTERS } from '~/components/ui/image.tsx'
import { Price } from '~/components/ui/price.tsx'
import { type GetProduct } from '~/models/ecommerce-providers/sylius/shop/fetch/product/get-product.ts'
import { type ProductVariantNormalized } from '~/models/ecommerce-providers/sylius/shop/interface/product-variant.ts'
import { type Context } from '~/routes/_layout/root.component.tsx'

/* -------------------------------------------------------------------------------------------------
 * I18next
 * -----------------------------------------------------------------------------------------------*/

enum I18NEXT {
	uiProductGridItem = 'ui-product-grid-item',
}

/* -------------------------------------------------------------------------------------------------
 * TEST_IDS
 * -----------------------------------------------------------------------------------------------*/

enum TEST_IDS {
	purchasable = 'purchasable',
}

/* -------------------------------------------------------------------------------------------------
 * ProductGridItem
 * -----------------------------------------------------------------------------------------------*/

type ProductGridItemProps = {
	product: GetProduct
	className?: string
  variant?: 'small' | 'default'
}

const ProductGridItem: React.FC<ProductGridItemProps> = (props) => {
	const { product, variant = 'default', className } = props

	const { locale } = useOutletContext<Context>()
	const { t } = useTranslation(I18NEXT.uiProductGridItem)

	return (
		<Card
			asChild
			size="3"
			className={cn('app-GridItem appImageShadowContainer', className)}
			data-purchasable={product.inStock}
		>
			<li>
				{!product.inStock ? (
					<Inset clip="padding-box" side="top" pb="current">
						<Box position="absolute" top="-4px">
							<Badge color="red">{t('items.badge.soldOut')}</Badge>
						</Box>
					</Inset>
				) : product.badges?.length ? (
          <Inset clip="padding-box" side="top" pb="current">
            <Flex align="center" gapX="1" position="absolute" top="-1px">
              {product.badges.map((badge) => (
                <Badge key={badge} color="red">{badge}</Badge>
              ))}
            </Flex>
          </Inset>
        ) : null}
				<Flex asChild direction="column" gapX="4" position="relative" mt="1">
					<Link
						prefetch="viewport"
						to={`/${locale}/products/${product.slug}`}
						aria-labelledby={`three-product-grid-item-${React.useId()}`}
					>
						<Flex align="center" justify="center" className="col-span-2">
							<Image
								alt={product.name!}
								src={product.images?.[0].path! || '/pwa/product-img-placeholder.svg'}
                api={!!product.images}
								filter={IMAGE_FILTERS.gridFilter}
								className="app-ImageShadow max-h-40"
								loading="lazy"
							/>
						</Flex>
						<Flex
							direction="column"
							justify="center"
							align="center"
							gapY="2"
							width="100%"
							className="col-span-3"
						>
							<ProductName
								mt="3"
								align="center"
								size="4"
							>
                {product.name!}
              </ProductName>
              {variant === 'default' && (
                <DataList.Root mt="3">
                  <DataList.Item align="center">
                    <DataList.Label minWidth="88px">{t('common.manufacturer')}</DataList.Label>
                    <DataList.Value>
                      {product.manufacturer}
                    </DataList.Value>
                  </DataList.Item>
                  {Object.entries(product.properties || {})?.map(([key, value]) => (
                    <DataList.Item key={key} align="center">
                      <DataList.Label minWidth="88px">{key}</DataList.Label>
                      <DataList.Value>
                        {value}
                      </DataList.Value>
                    </DataList.Item>
                  ))}
                </DataList.Root>
              )}

              <Flex justify="between" align="center" gapX="4" mt="5" className="grow">
								<Price
									locale={locale}
									color="gray"
									weight="bold"
									size={{
										initial: '3',
										md: '4',
									}}
								>
									{(product.defaultVariant as ProductVariantNormalized).price!}
								</Price>
							</Flex>
							<Box mt="3">
								<Button variant="ghost" size="2">
									{t('items.button.product')}
								</Button>
							</Box>
						</Flex>
					</Link>
				</Flex>
			</li>
		</Card>
	)
}
ProductGridItem.displayName = 'ProductGridItem'

/* -----------------------------------------------------------------------------------------------*/

export {
	ProductGridItem,
	I18NEXT as PRODUCT_GRID_ITEM_I18NEXT,
	TEST_IDS as PRODUCT_GRID_ITEM_TEST_IDS,
}
